import React, { useEffect, useState } from 'react';
import { Box, Avatar, useTheme, Tooltip, useMediaQuery, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store';
import UserSettingsMenu from './UserSettingsMenu';
import PendingRequestsBadge from './PendingRequestsBadge';
import { getProfileImage, fetchAndCacheProfileImage } from '../utils/profileImageCache';
import SettingsIcon from '@mui/icons-material/Settings';
import FeedbackIcon from '@mui/icons-material/Feedback';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PrintIcon from '@mui/icons-material/Print';
import { printConversation } from '@/utils/printConversation';

interface HeaderProps {
  user?: { name?: string; username?: string; picture?: string; idTokenClaims?: { name?: string; preferred_username?: string; picture?: string } } | null;
}

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  height: '56px',
  padding: theme.spacing(0, 0.5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0, 1.5),
  },
  borderBottom: '1px solid',
  borderColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.background.default : theme.palette.background.default,
}));

const Header: React.FC<HeaderProps> = ({ user }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const [cachedProfileImage, setCachedProfileImage] = useState<string | null>(null);
  const displayName = user?.idTokenClaims?.name || user?.name || user?.username || '';
  
  const currentUser = useAppStore(s => s.user);
  const selectedConversation = useAppStore(s => s.selectedConversation);
  const selectedAgent = useAppStore(s => s.selectedAgent);
  const agentList = useAppStore(s => s.agentList);
  const messages = useAppStore(s => s.messages);
  const currentFeature = useAppStore(s => s.currentFeature);
  const currentUserSettings = useAppStore(s => s.userSettings);
  const pendingPermissionRequestsCount = useAppStore(s => s.pendingPermissionRequestsCount);
  const hasPendingPermissionRequests =
    (currentUserSettings?.role === 'admin' || currentUserSettings?.role === 'manager' || currentUserSettings?.is_admin === true) &&
    pendingPermissionRequestsCount > 0;
  
  // Determine if we're in the chat view (where printing makes sense)
  const isInChatView = location.pathname === '/' && !currentFeature;
  
  // Get the current agent name for printing
  const getCurrentAgentName = () => {
    const agent = agentList.find(a => a.id === selectedAgent);
    return agent ? agent.name : 'AI Assistant';
  };
  
  // Handle print button click
  const handlePrint = () => {
    printConversation({
      conversationTitle: selectedConversation?.title || 'Conversation',
      agentName: getCurrentAgentName()
    });
  };
  

  
  // Single effect to load/fetch profile image
  useEffect(() => {
    let cancelled = false;
    
    const ensureImage = async () => {
      if (!currentUser?.id) {
        setCachedProfileImage(null);
        return;
      }
      
      // Try to get from cache first
      const cached = getProfileImage(currentUser.id);
      
      if (cached) {
        if (!cancelled) setCachedProfileImage(cached);
      } else {
        // Fetch from backend
        const fetched = await fetchAndCacheProfileImage(currentUser.id, false);
        if (!cancelled) {
          setCachedProfileImage(fetched || null);
        }
      }
    };
    
    ensureImage().catch(err => console.error('Failed to ensure profile image:', err));
    return () => { cancelled = true; };
  }, [currentUser?.id]);
  
  // Use cached image ONLY, ignore user.picture from props which is a server path
  const avatarUrl = cachedProfileImage || undefined;
  
  // (avatarKey removed - not used)

  // (No model fetching here; model selection happens in ChatInput and other components)

  return (
    <StyledHeader>
      {/* Left side - Bolt Logo */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        height: '36px',
        flexGrow: 1
      }}>
        <Tooltip title="Bolt - The Bossard AI Platform" arrow>
          <img 
            src="/images/Bolt.png"
            alt="Bolt - Bossard AI Platform" 
            style={{ 
              height: '42px',
              width: 'auto',
              objectFit: 'contain',
              display: 'block',
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          />
        </Tooltip>
      </Box>
      
      {/* Right side - User Avatar with Settings Menu */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        width: { xs: '56px', sm: '200px' },
        minWidth: { xs: '56px', sm: '200px' },
        justifyContent: 'flex-end' 
      }}>
        {/* Settings menu (cog) */}
        <UserSettingsMenu
          hasModelAccess={true}
          showThemeToggle={true}
          showUserSettings={true}
          showFeedback={false}
          showAdminPanel={true}
          showLogout={false}
          showDivider={false}
          trigger={
            <Tooltip title="Settings" arrow>
              <IconButton
                size="small"
                sx={{
                  position: 'relative',
                  color: 'primary.main',
                  transition: 'transform 0.25s, box-shadow 0.25s',
                  '&:hover': {
                    transform: 'scale(1.06)',
                    boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.5)'
                  },
                  '&:active': {
                    transform: 'scale(0.9)',
                    boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.8)'
                  }
                }}
                aria-label="Settings"
              >
                <SettingsIcon />
                {hasPendingPermissionRequests && (
                  <Box
                    component="span"
                    sx={{
                      position: 'absolute',
                      top: -2,
                      right: -6,
                    }}
                  >
                    <PendingRequestsBadge count={pendingPermissionRequestsCount} />
                  </Box>
                )}
              </IconButton>
            </Tooltip>
          }
        />

        {/* Print conversation button - only visible in chat view */}
        {isInChatView && (
          <Tooltip title={messages.length > 0 ? "Print Conversation" : "No conversation to print"} arrow>
            <span> {/* Wrapper needed for disabled button tooltip */}
              <IconButton
                size="small"
                disabled={messages.length === 0}
                sx={{
                  color: 'primary.main',
                  transition: 'transform 0.25s, box-shadow 0.25s',
                  '&:hover': {
                    transform: 'scale(1.06)',
                    boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.5)'
                  },
                  '&:active': {
                    transform: 'scale(0.9)',
                    boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.8)'
                  },
                  '&.Mui-disabled': {
                    color: 'action.disabled'
                  }
                }}
                aria-label="Print Conversation"
                onClick={handlePrint}
              >
                <PrintIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Feedback button - direct action to open feedback form */}
        <Tooltip title="Send Feedback" arrow>
          <IconButton
            size="small"
            sx={{
              color: 'primary.main',
              transition: 'transform 0.25s, box-shadow 0.25s',
              '&:hover': {
                transform: 'scale(1.06)',
                boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.5)'
              },
              '&:active': {
                transform: 'scale(0.9)',
                boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.8)'
              }
            }}
            aria-label="Send Feedback"
            onClick={() => {
              const url = 'https://bosnet.sharepoint.com/:l:/s/Innovation_Technology/JAC3MN96GytCQbFCBec0hbOaAfYBYVnHJMpga6rYDU5lfnA?nav=MmI0ODM0MGQtOTU3Zi00OTU5LWE1MDQtZGYyM2FiNWE4Zjky';
              window.open(url, '_blank', 'noopener,noreferrer');
            }}
          >
            <FeedbackIcon />
          </IconButton>
        </Tooltip>

        {/* Help button - direct action to open help page */}
        <Tooltip title="Help" arrow>
          <IconButton
            size="small"
            sx={{
              color: 'primary.main',
              transition: 'transform 0.25s, box-shadow 0.25s',
              '&:hover': {
                transform: 'scale(1.06)',
                boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.5)'
              },
              '&:active': {
                transform: 'scale(0.9)',
                boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.8)'
              }
            }}
            aria-label="Help"
            onClick={() => {
              const url = 'https://bosnet.sharepoint.com/sites/BOLT?xsdata=MDV8MDJ8fGRiMDcyNTE2YzQ2ZjRmYzlmOTQxMDhkZTVlNTZjNWExfDRlZTBiNmRiMjY4MjRmMGQ4MzU2YjhiNzFkNmFmMzM1fDB8MHw2MzkwNTE5MjM5MDkxMTQxNzd8VW5rbm93bnxWR1ZoYlhOVFpXTjFjbWwwZVZObGNuWnBZMlY4ZXlKRFFTSTZJbFJsWVcxelgwRlVVRk5sY25acFkyVmZVMUJQVEU5R0lpd2lWaUk2SWpBdU1DNHdNREF3SWl3aVVDSTZJbGRwYmpNeUlpd2lRVTRpT2lKUGRHaGxjaUlzSWxkVUlqb3hNWDA9fDF8TDJOb1lYUnpMekU1T20xbFpYUnBibWRmVFVSRmQwOUhWbWxOUkVGMFdXcE5NMDlUTURCUFYxRTFURlJzYWsxVWEzUk9SMUUwVFhwV2JVMUhXbXhaZW1zelFIUm9jbVZoWkM1Mk1pOXRaWE56WVdkbGN5OHhOelk1TlRrMU5UZzRORFUxfDI5YzQ3ZGY2M2E2ZjQ0ZWU2YzIwMDhkZTVlNTZjNWExfGZkMzQyMjE4NDYzNDQzNzU4YTI1ODgxOWY0OGMzZGU1&sdata=VjNKRjJ2eGx0V25EOXZrQklPc0IycU5CUWhhbVVxWVB6MXhCczZrMU9qVT0%3D&ovuser=4ee0b6db-2682-4f0d-8356-b8b71d6af335%2CRHINO%40bossard.com';
              window.open(url, '_blank', 'noopener,noreferrer');
            }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>

        {/* Profile avatar with Profile menu including Logout */}
        <UserSettingsMenu
          hasModelAccess={true}
          showThemeToggle={false}
          showUserSettings={false}
          showFeedback={false}
          showAdminPanel={false}
          showLogout={true}
          showDivider={false}
          trigger={
            <Tooltip title={currentUser?.name || displayName || 'User'} arrow>
              {avatarUrl ? (
                <img 
                  src={avatarUrl}
                  alt={currentUser?.name || displayName || 'User'}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    transition: 'transform 0.5s, box-shadow 0.5s',
                    display: 'block'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'scale(1.06)';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(25, 118, 210, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'scale(0.90)';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(25, 118, 210, 0.8)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'scale(1.06)';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(25, 118, 210, 0.5)';
                  }}
                />
              ) : (
                <Avatar 
                  alt={currentUser?.name || displayName || 'User'}
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    cursor: 'pointer',
                    transition: 'transform 0.5s, box-shadow 0.5s',
                    '&:hover': {
                      transform: 'scale(1.06)',
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.5)'
                    },
                    '&:active': {
                      transform: 'scale(0.90)',
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.8)'
                    }
                  }}
                />
              )}
            </Tooltip>
          }
        />
      </Box>
    </StyledHeader>
  );
};

export default Header;